import PageLayout from "@/components/layout/PageLayout";
import { getPerpsMaxBuilderFeesQueryKey } from "@/features/perpetuals/api/get-perps-max-builder-fees";
import { getPerpsReferralInfoQueryKey } from "@/features/perpetuals/api/get-perps-referral-info";
import PerpsActiveAssetDataListener from "@/features/perpetuals/api/listeners/perps-active-asset-data-listener";
import PerpsAssetCtxsListener from "@/features/perpetuals/api/listeners/perps-asset-ctxs-listener";
import PerpsTradeHistoryListener from "@/features/perpetuals/api/listeners/perps-trade-history-listener";
import PerpsUserDataListener from "@/features/perpetuals/api/listeners/perps-user-data-listener";
import { PerpsCoinProvider } from "@/features/perpetuals/api/providers/perps-coin-provider";
import { useHyperliquidWallet } from "@/features/wallets/api/get-wallets";
import { ChartOptionsProvider } from "@/lib/tradingview/chart-options-context";
import { Chain } from "@/models/enums/chain";
import { Spinner } from "@heroui/spinner";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { Suspense, useEffect, useRef } from "react";
import { PoolIdProvider } from "../trade/-providers/pool-id-provider";
import PerpetualsResizableSection from "./-components/layout/PerpetualsResizableSection";
import PerpetualsSidebar from "./-components/layout/PerpetualsSidebar";
import RecentPerpListener from "./-components/listeners/RecentPerpListener";
import { PerpetualsLimitCloseModalProvider } from "./-providers/perpetuals-limit-close-modal-provider";
import { PerpetualsMarketCloseModalProvider } from "./-providers/perpetuals-market-close-modal-provider";
import { PositionMarginModalProvider } from "./-providers/position-margin-modal-provider";
import { PositionTPSLModalProvider } from "./-providers/position-tpsl-modal-provider";

export const Route = createFileRoute("/_main/perpetuals/$coin")({
  head: () => ({
    meta: [{ title: "Reactor | Perpetuals" }],
  }),
  component: PerpetualsCoinPage,
});

function PerpetualsCoinPage() {
  const params = useParams({ from: "/_main/perpetuals/$coin" });
  const coin = params.coin as string;
  const hyperliquidWallet = useHyperliquidWallet();
  const queryClient = useQueryClient();
  const prefetched = useRef(false);

  useEffect(() => {
    if (hyperliquidWallet && !prefetched.current) {
      prefetched.current = true;
      queryClient.prefetchQuery({
        queryKey: getPerpsReferralInfoQueryKey(hyperliquidWallet.address),
      });

      queryClient.prefetchQuery({
        queryKey: getPerpsMaxBuilderFeesQueryKey(hyperliquidWallet.address),
      });
    }
  }, [hyperliquidWallet, queryClient]);

  return (
    <PageLayout>
      <Suspense
        fallback={
          <div className="flex h-screen items-center justify-center">
            <Spinner size="lg" color="secondary" />
          </div>
        }
      >
        <ChartOptionsProvider>
          <PerpsCoinProvider>
            <PerpsUserDataListener>
              <PerpsTradeHistoryListener>
                <PerpsActiveAssetDataListener>
                  <PerpsAssetCtxsListener>
                    <RecentPerpListener coin={coin}>
                      <PoolIdProvider poolId="" chain={Chain.solana}>
                        <PositionTPSLModalProvider>
                          <PositionMarginModalProvider>
                            <PerpetualsMarketCloseModalProvider>
                              <PerpetualsLimitCloseModalProvider>
                                <div className="flex h-full max-h-full w-full gap-1.5">
                                  <PerpetualsResizableSection />
                                  <PerpetualsSidebar />
                                </div>
                              </PerpetualsLimitCloseModalProvider>
                            </PerpetualsMarketCloseModalProvider>
                          </PositionMarginModalProvider>
                        </PositionTPSLModalProvider>
                      </PoolIdProvider>
                    </RecentPerpListener>
                  </PerpsAssetCtxsListener>
                </PerpsActiveAssetDataListener>
              </PerpsTradeHistoryListener>
            </PerpsUserDataListener>
          </PerpsCoinProvider>
        </ChartOptionsProvider>
      </Suspense>
    </PageLayout>
  );
}
